import { useState } from 'react';

import { Alert, Badge, Button, Group, Loader, Modal, ScrollArea, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

import { useProfileReviews } from '@/hooks/useProfileReviews';
import type { ProfileDecision, SubjectProfile } from '@/types/profile-review';
import { AddProfileForm } from './AddProfileForm';
import { ProfileReviewCard } from './ProfileReviewCard';

export interface ProfileReviewPanelProps {
  reportId: string;
  /** Fired after a successful submit — the page refreshes the report so
   * the gate alert clears and the stage badge updates. */
  onSubmitted?: () => void | Promise<void>;
  /** Admins see the deployment setting (shadow mode); clients never do. */
  isAdmin?: boolean;
}

function isUndecided(profile: SubjectProfile): boolean {
  return profile.status === 'pending' && profile.decision === null;
}

/** Undecided cards first, each group in the server's order — so a card
 * sinks below the queue the moment it is decided and the next undecided
 * one takes its place under the reviewer's cursor. Keys are stable, so the
 * moved card keeps its note. */
export function orderForReview(profiles: SubjectProfile[]): SubjectProfile[] {
  return [...profiles.filter(isUndecided), ...profiles.filter((p) => !isUndecided(p))];
}

/**
 * The gate before a subject's social media screening: every queued profile
 * for every subject, with Confirm / Reject per row, a way to add a profile
 * the system missed, and a single Submit that releases the confirmed
 * profiles to the screening order and lets the report continue.
 */
export function ProfileReviewPanel({
  reportId,
  onSubmitted,
  isAdmin = false,
}: Readonly<ProfileReviewPanelProps>) {
  const { data, isLoading, isMutating, error, decide, add, rejectRest, submit } =
    useProfileReviews(reportId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rejectRestOpen, setRejectRestOpen] = useState(false);

  if (isLoading && !data) {
    return (
      <Group gap="xs" p="md">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Loading profiles…
        </Text>
      </Group>
    );
  }

  if (error && !data) {
    return (
      <Alert
        color="red"
        icon={<IconAlertCircle size={16} />}
        title="Could not load profile reviews"
      >
        {error.message}
      </Alert>
    );
  }

  const list = data;
  const subjects = list?.subjects ?? [];
  const pending = list?.pendingCount ?? 0;
  const undecided = list?.undecidedCount ?? 0;
  const canSubmit = pending > 0 && undecided === 0 && !isMutating;
  // The deployment setting is an admin's concern only. `off` is named
  // separately: no queue is built then, so nothing is being recorded.
  const setting = isAdmin ? list?.setting : undefined;

  const handleDecide =
    (rowId: string) => async (decision: ProfileDecision, note?: string | null) => {
      try {
        await decide(rowId, decision, note);
      } catch {
        // toasted by the hook
      }
    };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    try {
      await submit();
      await onSubmitted?.();
    } catch {
      // toasted by the hook
    }
  };

  const handleRejectRest = async () => {
    setRejectRestOpen(false);
    try {
      await rejectRest();
    } catch {
      // toasted by the hook
    }
  };

  return (
    <Stack gap="xs" style={{ minHeight: 0, flex: 1 }}>
      <Stack gap={4}>
        <Text size="sm" data-testid="profile-review-counts">
          {pending > 0 ? (
            <>
              <strong>{pending}</strong> profile{pending === 1 ? '' : 's'} waiting ·{' '}
              <strong>{undecided}</strong> undecided
            </>
          ) : (
            'No profiles are waiting for review.'
          )}
        </Text>
        {setting === 'shadow' && (
          <Text size="xs" c="dimmed" data-testid="profile-review-shadow">
            Shadow mode: decisions are recorded, the screening is not held.
          </Text>
        )}
        {setting === 'off' && (
          <Text size="xs" c="dimmed" data-testid="profile-review-off">
            Profile review is off on this deployment: no profiles are queued.
          </Text>
        )}
      </Stack>

      {pending > 0 && (
        <Group gap="xs">
          {/* The twin of match review's "Accept AI decisions for the rest":
              a public figure brings a pile of namesakes, so the reviewer
              confirms the profiles that are the subject's and clears the
              rest in one click. */}
          {undecided > 0 && (
            <Button
              size="xs"
              variant="default"
              disabled={isMutating}
              onClick={() => setRejectRestOpen(true)}
              data-testid="reject-rest-profile-review"
            >
              Reject the rest
            </Button>
          )}
          <Button
            size="xs"
            disabled={!canSubmit}
            loading={isMutating}
            onClick={() => setConfirmOpen(true)}
            data-testid="submit-profile-review"
          >
            Submit decisions
          </Button>
        </Group>
      )}

      <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars>
        <Stack gap="md">
          {subjects.map((subject) => (
            <Stack gap="xs" key={subject.subjectId}>
              <Group gap="xs" wrap="wrap">
                <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                  {subject.subjectName || 'Subject'} · {subject.profiles.length}
                </Text>
                {subject.orderPlaced && (
                  <>
                    <Badge variant="light" color="gray" size="xs" style={{ textTransform: 'none' }}>
                      Screening already ordered
                    </Badge>
                    {/* In the flow of text, not a tooltip: a badge takes no
                        focus, so a tooltip never reaches keyboard or screen
                        reader users, and this is the one line that says what
                        a late confirmation does. */}
                    <Text size="xs" c="dimmed">
                      Confirmations are recorded; nothing is re-ordered.
                    </Text>
                  </>
                )}
              </Group>
              {orderForReview(subject.profiles).map((profile) => (
                <ProfileReviewCard
                  key={profile.id}
                  profile={profile}
                  disabled={isMutating}
                  showSystemDetail={isAdmin}
                  onDecide={handleDecide(profile.id)}
                />
              ))}
              <AddProfileForm subjectId={subject.subjectId} disabled={isMutating} onAdd={add} />
            </Stack>
          ))}
        </Stack>
      </ScrollArea>

      <Modal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Submit ${pending} decision${pending === 1 ? '' : 's'}?`}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Confirmed profiles will be screened, rejected ones will not. Decisions cannot be changed
            after submitting.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button size="xs" onClick={handleSubmit} data-testid="confirm-submit-profile-review">
              Submit
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={rejectRestOpen}
        onClose={() => setRejectRestOpen(false)}
        title={`Reject the ${undecided} undecided profile${undecided === 1 ? '' : 's'}?`}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            {undecided === 1 ? 'It' : 'They'} will not be screened. You can still change any
            decision before submitting.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setRejectRestOpen(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              color="red"
              onClick={handleRejectRest}
              data-testid="confirm-reject-rest-profile-review"
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
